import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import {
  AccessTimeRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  LocationOnRounded,
  NotesRounded,
  SaveRounded,
  ScheduleRounded,
  WarningAmberRounded,
} from "@mui/icons-material";

import { useSelector } from "react-redux";

import {
  GetActiveTemporaryClosureReasons,
  CreateTemporaryClosure,
} from "../api/TemporaryClosureApi";

const TemporaryClosures = () => {
  // =========================================================
  // REDUX
  // =========================================================

  const token = useSelector((state) => state.user?.token);
  const lang = useSelector((state) => state.user?.lang || "FR");
  const location = useSelector((state) => state.user?.location);
  const username = useSelector((state) => state.user?.username);

  // =========================================================
  // TRANSLATION
  // =========================================================

  const t = (fr, en) => (lang === "EN" ? en : fr);

  // =========================================================
  // LOCATION
  // =========================================================

  const noEmplacement = location?.noEmplacement;

  const locationLabel = useMemo(() => {
    if (!location) return "";

    const name = location.nom || location.nomEmplacement || location.name || "";

    const accpac = location.noAccpac || location.referenceCode || "";

    if (name && accpac) {
      return `${name} | ${accpac}`;
    }

    if (name) {
      return name;
    }

    if (accpac) {
      return accpac;
    }

    return noEmplacement ? String(noEmplacement) : "";
  }, [location, noEmplacement]);

  // =========================================================
  // STATE
  // =========================================================

  const [reasons, setReasons] = useState([]);

  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");

  const [reasonId, setReasonId] = useState("");

  const [comment, setComment] = useState("");

  const [loadingReasons, setLoadingReasons] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errors, setErrors] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // =========================================================
  // MIN START DATE
  // Maximum 1 month in the past
  // =========================================================

  const minStartDateTime = useMemo(() => {
    const date = new Date();

    date.setMonth(date.getMonth() - 1);
    date.setSeconds(0);
    date.setMilliseconds(0);

    const offset = date.getTimezoneOffset();

    const localDate = new Date(date.getTime() - offset * 60000);

    return localDate.toISOString().slice(0, 16);
  }, []);

  // =========================================================
  // LOAD ACTIVE REASONS
  // =========================================================

  useEffect(() => {
    const loadReasons = async () => {
      if (!token) return;

      setLoadingReasons(true);

      try {
        const result = await GetActiveTemporaryClosureReasons(token);

        /*
         * API expected response:
         *
         * [
         *   {
         *     id: 1,
         *     nomFr: "Maintenance",
         *     nomEn: "Maintenance"
         *   }
         * ]
         */

        const data = Array.isArray(result) ? result : result?.data || [];

        setReasons(data);
      } catch (error) {
        console.error("Load temporary closure reasons:", error);

        setSnackbar({
          open: true,
          severity: "error",
          message:
            error.message ||
            t(
              "Impossible de récupérer les raisons de fermeture.",
              "Unable to retrieve closure reasons.",
            ),
        });
      } finally {
        setLoadingReasons(false);
      }
    };

    loadReasons();
  }, [token, lang]);

  // =========================================================
  // REASON LABEL
  // =========================================================

  const getReasonLabel = (reason) => {
    if (!reason) return "";

    return lang === "EN" ? reason.nomEn || "" : reason.nomFr || "";
  };

  // =========================================================
  // SELECTED REASON
  // =========================================================

  const selectedReason = useMemo(() => {
    return reasons.find((reason) => String(reason.id) === String(reasonId));
  }, [reasons, reasonId]);

  // =========================================================
  // FORMAT DATE FOR SUMMARY
  // =========================================================

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(lang === "EN" ? "en-CA" : "fr-CA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  // =========================================================
  // VALIDATION
  // =========================================================

  const validate = () => {
    const newErrors = {};

    // ---------------------------------------------------------
    // LOCATION
    // ---------------------------------------------------------

    if (!noEmplacement) {
      newErrors.location = t(
        "Aucun emplacement n'est sélectionné.",
        "No location is selected.",
      );
    }

    // ---------------------------------------------------------
    // START
    // ---------------------------------------------------------

    if (!startDateTime) {
      newErrors.startDateTime = t(
        "Veuillez sélectionner une date et une heure de début.",
        "Please select a start date and time.",
      );
    }

    // ---------------------------------------------------------
    // END
    // ---------------------------------------------------------

    if (!endDateTime) {
      newErrors.endDateTime = t(
        "Veuillez sélectionner une date et une heure de fin.",
        "Please select an end date and time.",
      );
    }

    // ---------------------------------------------------------
    // START MAXIMUM ONE MONTH IN THE PAST
    // ---------------------------------------------------------

    if (startDateTime) {
      const start = new Date(startDateTime);
      const minDate = new Date(minStartDateTime);

      if (start < minDate) {
        newErrors.startDateTime = t(
          "La date de début ne peut pas être plus d'un mois dans le passé.",
          "The start date cannot be more than one month in the past.",
        );
      }
    }

    // ---------------------------------------------------------
    // END > START
    // ---------------------------------------------------------

    if (startDateTime && endDateTime) {
      const start = new Date(startDateTime);
      const end = new Date(endDateTime);

      if (end <= start) {
        newErrors.endDateTime = t(
          "La date de fin doit être postérieure à la date de début.",
          "The end date must be later than the start date.",
        );
      }
    }

    // ---------------------------------------------------------
    // REASON
    // ---------------------------------------------------------

    if (!reasonId) {
      newErrors.reasonId = t(
        "Veuillez sélectionner une raison.",
        "Please select a reason.",
      );
    }

    // ---------------------------------------------------------
    // COMMENT
    // ---------------------------------------------------------

    if (comment.length > 500) {
      newErrors.comment = t(
        "Le commentaire ne peut pas dépasser 500 caractères.",
        "The comment cannot exceed 500 characters.",
      );
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    // =========================================================
    // VALIDATION FRONTEND
    // =========================================================

    if (!validate()) {
      return;
    }

    // =========================================================
    // VALIDATION USERNAME
    // =========================================================

    if (!username) {
      setSnackbar({
        open: true,
        severity: "error",
        message: t(
          "Impossible d'identifier l'utilisateur connecté.",
          "Unable to identify the logged-in user.",
        ),
      });

      return;
    }

    // =========================================================
    // VALIDATION TOKEN
    // =========================================================

    if (!token) {
      setSnackbar({
        open: true,
        severity: "error",
        message: t(
          "Votre session a expiré. Veuillez vous reconnecter.",
          "Your session has expired. Please log in again.",
        ),
      });

      return;
    }

    setSaving(true);

    try {
      // =======================================================
      // PAYLOAD
      // =======================================================

      const payload = {
        noEmplacement: Number(noEmplacement),

        dateDebut: startDateTime,

        dateFin: endDateTime,

        idFermetureRaison: Number(reasonId),

        commentaire: comment.trim() || null,

        username: username,
      };

      console.log("CreateTemporaryClosure payload:", payload);

      // =======================================================
      // API
      // =======================================================

      await CreateTemporaryClosure(token, payload);

      // =======================================================
      // SUCCESS
      // =======================================================

      setSnackbar({
        open: true,
        severity: "success",
        message: t(
          "La fermeture temporaire a été notée dans le système.",
          "The temporary closure has been noted in the system.",
        ),
      });

      // =======================================================
      // RESET FORM
      // =======================================================

      setStartDateTime("");
      setEndDateTime("");
      setReasonId("");
      setComment("");
      setErrors({});
    } catch (error) {
      console.error("Create temporary closure:", error);

      setSnackbar({
        open: true,
        severity: "error",
        message:
          error?.message ||
          t(
            "Une erreur est survenue lors de l'enregistrement de la fermeture temporaire.",
            "An error occurred while saving the temporary closure.",
          ),
      });
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // CLEAR ERROR
  // =========================================================

  const clearError = (field) => {
    setErrors((previous) => ({
      ...previous,
      [field]: undefined,
    }));
  };

  // =========================================================
  // RESET FORM
  // =========================================================

  const handleReset = () => {
    if (saving) return;

    setStartDateTime("");
    setEndDateTime("");
    setReasonId("");
    setComment("");
    setErrors({});
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: "#f6f7f9",
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 3,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: "auto",
        }}
      >
        {/* ===================================================
            HEADER
        ==================================================== */}

        <Box sx={{ mb: 3.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              letterSpacing: 0.4,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            {t("Administration", "Administration")}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: -0.5,
              mb: 0.75,
            }}
          >
            {t("Fermetures temporaires", "Temporary Closures")}
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 720,
            }}
          >
            {t(
              "Planifiez une période de fermeture temporaire pour l'emplacement actuellement sélectionné.",
              "Schedule a temporary closure period for the currently selected location.",
            )}
          </Typography>
        </Box>

        {/* ===================================================
            MAIN CARD
        ==================================================== */}

        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 2.5,
                sm: 4,
                md: 5,
              },
              "&:last-child": {
                pb: {
                  xs: 2.5,
                  sm: 4,
                  md: 5,
                },
              },
            }}
          >
            <Box component="form" onSubmit={handleSubmit}>
              {/* =================================================
                  LOCATION
              ================================================== */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: 2.5,
                  mb: 4,
                  borderRadius: 2.5,
                  backgroundColor: "#f8fafc",
                  border: "1px solid",
                  borderColor: "#e8ebef",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backgroundColor: "#eef2f7",
                  }}
                >
                  <LocationOnRounded
                    sx={{
                      color: "text.secondary",
                    }}
                  />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      color: "text.secondary",
                      letterSpacing: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    {t("Emplacement", "Location")}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      mt: 0.5,
                      fontWeight: 650,
                      wordBreak: "break-word",
                    }}
                  >
                    {locationLabel ||
                      t(
                        "Aucun emplacement sélectionné",
                        "No location selected",
                      )}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                    }}
                  >
                    {t(
                      "Emplacement actuellement sélectionné",
                      "Currently selected location",
                    )}
                  </Typography>
                </Box>
              </Box>

              {errors.location && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {errors.location}
                </Alert>
              )}

              {/* =================================================
                  SECTION PERIOD
              ================================================== */}

              <Box sx={{ mb: 4 }}>
                <SectionHeader
                  icon={<ScheduleRounded />}
                  title={t("Période de fermeture", "Closure period")}
                  subtitle={t(
                    "Définissez précisément la période pendant laquelle l'emplacement sera fermé.",
                    "Define precisely when the location will be closed.",
                  )}
                />

                <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                  {/* START */}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label={t("Début", "Start")}
                      value={startDateTime}
                      onChange={(event) => {
                        setStartDateTime(event.target.value);

                        clearError("startDateTime");
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: minStartDateTime,
                        step: 60,
                      }}
                      error={Boolean(errors.startDateTime)}
                      helperText={
                        errors.startDateTime ||
                        t(
                          "Date et heure, à la minute près.",
                          "Date and time, to the minute.",
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthRounded
                              fontSize="small"
                              color="action"
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* END */}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label={t("Fin", "End")}
                      value={endDateTime}
                      onChange={(event) => {
                        setEndDateTime(event.target.value);

                        clearError("endDateTime");
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 60,
                      }}
                      error={Boolean(errors.endDateTime)}
                      helperText={
                        errors.endDateTime ||
                        t(
                          "La fin doit être après le début.",
                          "End time must be after start time.",
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeRounded
                              fontSize="small"
                              color="action"
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* DATE INFO */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 2,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    width: "fit-content",
                  }}
                >
                  <WarningAmberRounded
                    sx={{
                      fontSize: 18,
                      color: "text.secondary",
                    }}
                  />

                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "La date de début peut remonter jusqu'à un mois dans le passé.",
                      "The start date can be up to one month in the past.",
                    )}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* =================================================
                  SECTION DETAILS
              ================================================== */}

              <Box sx={{ mb: 4 }}>
                <SectionHeader
                  icon={<NotesRounded />}
                  title={t("Détails", "Details")}
                  subtitle={t(
                    "Indiquez la raison de la fermeture et ajoutez une note si nécessaire.",
                    "Select the reason for the closure and add a note if needed.",
                  )}
                />

                <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                  {/* REASON */}

                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={Boolean(errors.reasonId)}
                      disabled={loadingReasons}
                    >
                      <InputLabel>{t("Raison", "Reason")}</InputLabel>

                      <Select
                        value={reasonId}
                        label={t("Raison", "Reason")}
                        onChange={(event) => {
                          setReasonId(event.target.value);

                          clearError("reasonId");
                        }}
                      >
                        {loadingReasons ? (
                          <MenuItem disabled>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress size={18} />

                              {t(
                                "Chargement des raisons...",
                                "Loading reasons...",
                              )}
                            </Box>
                          </MenuItem>
                        ) : reasons.length === 0 ? (
                          <MenuItem disabled>
                            {t(
                              "Aucune raison active disponible.",
                              "No active reason available.",
                            )}
                          </MenuItem>
                        ) : (
                          reasons.map((reason) => (
                            <MenuItem key={reason.id} value={reason.id}>
                              {getReasonLabel(reason)}
                            </MenuItem>
                          ))
                        )}
                      </Select>

                      {errors.reasonId && (
                        <FormHelperText>{errors.reasonId}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* COMMENT */}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={8}
                      label={t("Commentaire", "Comment")}
                      placeholder={t(
                        "Ajoutez un commentaire facultatif...",
                        "Add an optional comment...",
                      )}
                      value={comment}
                      onChange={(event) => {
                        const value = event.target.value;

                        if (value.length <= 500) {
                          setComment(value);
                        }

                        clearError("comment");
                      }}
                      error={Boolean(errors.comment)}
                      helperText={errors.comment || `${comment.length} / 500`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{
                              alignSelf: "flex-start",
                              mt: 1.5,
                            }}
                          >
                            <NotesRounded fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* =================================================
                  SUMMARY
              ================================================== */}

              {(startDateTime || endDateTime || selectedReason) && (
                <>
                  <Divider sx={{ mb: 3 }} />

                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: "#e5e7eb",
                      backgroundColor: "#fafafa",
                      p: 2.5,
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <CheckCircleRounded
                        sx={{
                          fontSize: 20,
                          color: "text.secondary",
                        }}
                      />

                      <Typography variant="subtitle1" fontWeight={700}>
                        {t("Résumé de la fermeture", "Closure summary")}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <SummaryItem
                        label={t("Emplacement", "Location")}
                        value={locationLabel || "—"}
                      />

                      <SummaryItem
                        label={t("Début", "Start")}
                        value={startDateTime ? formatDate(startDateTime) : "—"}
                      />

                      <SummaryItem
                        label={t("Fin", "End")}
                        value={endDateTime ? formatDate(endDateTime) : "—"}
                      />

                      <SummaryItem
                        label={t("Raison", "Reason")}
                        value={
                          selectedReason ? getReasonLabel(selectedReason) : "—"
                        }
                      />

                      {comment.trim() && (
                        <SummaryItem
                          label={t("Commentaire", "Comment")}
                          value={comment.trim()}
                        />
                      )}
                    </Grid>
                  </Box>
                </>
              )}

              {/* =================================================
                  ACTIONS
              ================================================== */}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1.5,
                  pt: 1,
                }}
              >
                <Button
                  type="button"
                  variant="text"
                  color="inherit"
                  disabled={saving}
                  onClick={handleReset}
                  sx={{
                    px: 2.5,
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {t("Réinitialiser", "Reset")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || loadingReasons}
                  startIcon={
                    saving ? (
                      <CircularProgress size={19} color="inherit" />
                    ) : (
                      <SaveRounded />
                    )
                  }
                  sx={{
                    px: 3,
                    py: 1.25,
                    borderRadius: 1.75,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  {saving
                    ? t("Enregistrement...", "Saving...")
                    : t("Enregistrer la fermeture", "Save closure")}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* =====================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() =>
          setSnackbar((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((previous) => ({
              ...previous,
              open: false,
            }))
          }
          sx={{
            minWidth: {
              xs: "auto",
              sm: 400,
            },
            borderRadius: 2,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// =========================================================
// SECTION HEADER
// =========================================================

const SectionHeader = ({ icon, title, subtitle }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          {React.cloneElement(icon, {
            fontSize: "small",
          })}
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  );
};

// =========================================================
// SUMMARY ITEM
// =========================================================

const SummaryItem = ({ label, value }) => {
  return (
    <Grid item xs={12} sm={6}>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mb: 0.35,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Grid>
  );
};

export default TemporaryClosures;
